import {
  faCircleCheck,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert, Button } from '@orobix/orobix-fe-components'
import { useTranslation } from 'react-i18next'

import { DrugDimension } from '../../../types/drugRegistry'
import { FilteringValue } from '../../../types/filtering'
import { useStyles } from './GeometryAnomaliesPanel.styles'
import { useGeometryAnomalies } from './useGeometryAnomalies'

const DIMENSION_LABELS = {
  height: 'Height',
  width: 'Width',
  depth: 'Depth',
} as const

type GeometryAnomaliesPanelProps = {
  /** Whether the table is currently narrowed to the anomalous drugs. */
  isFilterActive: boolean
  onFilter: (value: FilteringValue) => void
}

/**
 * Info panel above the registry table: how many drugs have an unusable
 * geometry (at least one of height/width/depth equal to 0 or NaN), with the
 * per-dimension breakdown and a shortcut to list just those drugs.
 */
export const GeometryAnomaliesPanel = ({
  isFilterActive,
  onFilter,
}: GeometryAnomaliesPanelProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { data } = useGeometryAnomalies()

  // A failed or pending count is not worth a banner of its own: the table
  // right below is what the page is for.
  if (!data) return null

  if (data.total === 0) {
    return (
      <Alert type="success" withBorder>
        <span className={classes.icon}>
          <FontAwesomeIcon icon={faCircleCheck} />
        </span>
        {t('No drug geometry anomalies')}
      </Alert>
    )
  }

  return (
    <Alert type="warning" withBorder>
      <div className={classes.content}>
        <div>
          <span className={classes.icon}>
            <FontAwesomeIcon icon={faTriangleExclamation} />
          </span>
          <strong>
            {t('{{ count }} drugs with geometry anomalies', {
              count: data.total,
            })}
          </strong>{' '}
          {t('(at least one dimension equal to 0 or NaN)')}
          <ul className={classes.breakdown}>
            {(Object.keys(DIMENSION_LABELS) as DrugDimension[]).map((key) => (
              <li key={key}>
                {t(DIMENSION_LABELS[key])}:{' '}
                {t(
                  '{{ zero }} equal to 0, {{ nan }} NaN',
                  data.byDimension[key]
                )}
              </li>
            ))}
          </ul>
        </div>
        <Button
          label={
            isFilterActive ? t('Show all drugs') : t('Show anomalies only')
          }
          onClick={() => onFilter(isFilterActive ? undefined : 'true')}
        />
      </div>
    </Alert>
  )
}
